<script setup lang="ts">
import type { EventUpgradePackage } from "@/models"
import cover from "@brand-cover"
import { getBrandFooterUrls } from "@/components/Footer/footer.constants"
import { ButtonLink, Chip, Icon } from "@/components/ui"
import { useTranslation } from "@/composables"

interface UpgradeCardProps {
  eventVaultId?: number
  eventUpgrade?: EventUpgradePackage
  isLoading?: boolean
}

withDefaults(defineProps<UpgradeCardProps>(), {
  eventVaultId: undefined,
  eventUpgrade: undefined,
  isLoading: false,
})

const { t } = useTranslation()

const helpCenterUrl = getBrandFooterUrls().helpCenter
</script>

<template>
  <div v-if="isLoading" class="border border-[#dfdfdf] rounded-lg w-full sm:max-w-[200px] md:max-w-[250px] flex flex-col gap-2 overflow-hidden">
    <div class="gpx-skeleton h-[250px]" />
    <div class="flex flex-col gap-2 p-4">
      <div class="gpx-skeleton h-10" />
      <div class="gpx-skeleton h-10" />
    </div>
  </div>

  <div v-else-if="eventUpgrade" class="border border-[#dfdfdf] rounded-lg w-full sm:max-w-[200px] md:max-w-[250px] flex flex-col gap-2 overflow-hidden">
    <img v-if="eventUpgrade.imageUrl" :src="eventUpgrade.imageUrl" :alt="t('event_upgrade.upgrade_card.cover_image_alt')">
    <img v-else :src="cover" :alt="t('event_upgrade.upgrade_card.default_cover_image_alt')">
    <div class="flex flex-col gap-4 p-4 h-full">
      <div class="grow flex flex-col gap-1">
        <h4 class="font-semibold font-comfortaa" data-test="package-name">
          <a v-if="eventUpgrade.productLink" :href="eventUpgrade.productLink" target="_blank" class="hover:underline">{{ eventUpgrade.name }}</a>
          <span v-else>
            {{ eventUpgrade.name }}
          </span>
        </h4>
        <p data-test="package-description">
          {{ eventUpgrade.description }}
        </p>
        <Chip v-if="eventUpgrade.guestCount" data-test="package-guests-count" class="w-fit text-sm">
          <Icon size="sm" icon="User" />
          {{ eventUpgrade.guestCount }}
        </Chip>
      </div>
      <ButtonLink
        v-if="eventUpgrade?.purchaseLink || eventVaultId"
        :href="eventUpgrade?.purchaseLink"
        :to="!eventUpgrade?.purchaseLink ? `/event/${eventVaultId}/upgrade/contact-us` : undefined"
        data-test="purchase-link"
      >
        {{ eventUpgrade?.purchaseLink
          ? t("event_upgrade.upgrade_card.buy_now")
          : t("event_upgrade.upgrade_card.contact_us")
        }}
      </ButtonLink>
      <ButtonLink
        v-else
        :href="helpCenterUrl"
        data-test="help-link"
      >
        {{ t("event_upgrade.upgrade_card.contact_us") }}
      </ButtonLink>
    </div>
  </div>
</template>
